import { forwardRef, useRef, useEffect } from 'react';
import type { PropsWithoutRef, Ref, MouseEvent, MouseEventHandler } from 'react';
import styled from 'styled-components';

import type { BaseProps, ForwardProps, ForwardRefForwardPropsComponent } from '../../types';
import { defaultThemeProp } from '../../theme';
import { useConsolidatedRef } from '../../hooks';

import type { ButtonProps } from './Button';

export interface BareButtonProps extends BaseProps {
  /** Text or content for the Button. */
  children: ButtonProps['children'];
  /**
   * Determines if the Button will be disabled.
   * @default false
   */
  disabled?: ButtonProps['disabled'];
  /** A location to navigate to. Passing an href will render a Link styled as a Button. */
  href?: ButtonProps['href'];
  /**
   * Controls the behavior of a Button within a Form.
   * @default 'button'
   */
  type?: ButtonProps['type'];
  /** Ref forwarded to the wrapping element. */
  ref?: ButtonProps['ref'];
}

export const StyledBareButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  user-select: none;
  color: inherit;

  @media (forced-colors: none) {
    outline: none;
  }

  & + & {
    margin-inline-start: ${props => props.theme.base.spacing};
  }
`;

StyledBareButton.defaultProps = defaultThemeProp;

const BareButton: ForwardRefForwardPropsComponent<BareButtonProps> = forwardRef(function BareButton(
  {
    type = 'button',
    disabled = false,
    href,
    ...restProps
  }: PropsWithoutRef<BareButtonProps> & ForwardProps,
  ref: BareButtonProps['ref']
) {
  const mouseDownEvent = useRef<MouseEvent>();
  const buttonRef = useConsolidatedRef(ref);

  useEffect(() => {
    return () => {
      mouseDownEvent.current = undefined;
    };
  }, []);

  const handleMouseDown = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    (restProps.onMouseDown as MouseEventHandler | undefined)?.(e);
    e.persist();
    mouseDownEvent.current = e;
  };

  const handleMouseUp = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    const shouldFocus = mouseDownEvent.current && !mouseDownEvent.current.defaultPrevented;
    mouseDownEvent.current = undefined;

    if (shouldFocus && buttonRef.current !== document.activeElement) {
      buttonRef.current?.focus();
    }

    return (restProps.onMouseUp as MouseEventHandler | undefined)?.(e);
  };

  if (href) {
    return (
      <StyledBareButton
        as='a'
        ref={buttonRef as Ref<HTMLAnchorElement>}
        href={href}
        // Not actually included in the types for a, but we've been setting it, so we should continue for now.
        {...{ disabled }}
        {...restProps}
        onMouseDown={handleMouseDown}
        onMouseDownCapture={handleMouseUp}
      />
    );
  }

  return (
    <StyledBareButton
      ref={buttonRef as Ref<HTMLButtonElement>}
      type={type}
      disabled={disabled}
      {...restProps}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    />
  );
});

export default BareButton;
